import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Package,
  Store,
  Archive,
  ArchiveRestore,
  Info,
  X,
  RefreshCw,
  Image as ImageIcon,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import SearchableDropdown from "../components/SearchableDropdown";
import ImageUpload from "@/components/ImageUpload";
import { mastersApi, vendorsApi, apiErrorMessage, type ApiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatCurrencyFull } from "@/lib/data";
import { getImageUrl, byNewest } from "@/lib/utils";
import { CHARGE_UNITS, type ChargeUnit, toUnit, unitSuffix, unitWords, marginOf, type MarginLevel } from "@/lib/pricing";

// ─── Types ──────────────────────────────────────────────────────────────────

interface Service {
  id: number;
  name: string;
  category: string | null;
  description: string | null;
  /** Laravel decimal: arrives as a string */
  selling_price: string | number;
  /** null = cost not set */
  base_price: string | number | null;
  unit: ChargeUnit;
  vendor_id: number | null;
  vendor: { id: number; name: string } | null;
  /** Storage path, shown via getImageUrl */
  image: string | null;
  is_active: boolean;
  packages_count: number;
  events_count: number;
  quotations_count: number;
  created_at?: string | null;
}

interface VendorOption {
  id: number;
  name: string;
}

type SortKey = "newest" | "name" | "price_desc" | "price_asc" | "margin_asc" | "margin_desc";

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "name", label: "Name (A–Z)" },
  { value: "price_desc", label: "Price (high to low)" },
  { value: "price_asc", label: "Price (low to high)" },
  { value: "margin_asc", label: "Margin (lowest first)" },
  { value: "margin_desc", label: "Margin (highest first)" },
];

/** Category chip value for services without a category */
const UNCATEGORISED = "__none__";

// ─── Helpers ────────────────────────────────────────────────────────────────

const hasValue = (v: unknown) => v !== null && v !== undefined && String(v).trim() !== "";
const num = (v: unknown) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};
/** "" → null (not entered), "1.2.3" → NaN (invalid), "1,250" → 1250 */
const parseMoney = (v: string): number | null => {
  const t = v.replace(/,/g, "").trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
};
const toBool = (v: unknown, fallback: boolean) =>
  v === null || v === undefined ? fallback : v === true || v === 1 || v === "1" || v === "true";

/** ₹1,250 or −₹1,250 */
const money = (n: number) => `${n < 0 ? "−" : ""}${formatCurrencyFull(Math.abs(n))}`;
/** 36 → "36%", 29.63 → "29.6%" */
const pctText = (p: number) => `${Number(p.toFixed(1))}%`;
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Same comparison as the server's Names::normalize: case, punctuation, accents, "&" and "and" ignored */
const normalizeName = (name: string) =>
  name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .split(/[^a-z0-9]+/)
    .filter((w) => w && w !== "and")
    .join("-");

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const listOf = (res: any): any[] =>
  Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res) ? res : [];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toService = (raw: any): Service => ({
  id: Number(raw.id),
  name: String(raw.name ?? ""),
  category: raw.category ? String(raw.category) : null,
  description: raw.description ? String(raw.description) : null,
  selling_price: raw.selling_price ?? 0,
  base_price: hasValue(raw.base_price) ? raw.base_price : null,
  unit: toUnit(raw.unit),
  vendor_id: raw.vendor_id ? Number(raw.vendor_id) : null,
  vendor: raw.vendor ?? null,
  image: raw.image || null,
  is_active: toBool(raw.is_active, true),
  packages_count: Number(raw.packages_count) || 0,
  events_count: Number(raw.events_count) || 0,
  quotations_count: Number(raw.quotations_count) || 0,
  created_at: raw.created_at ?? null,
});

/** Margin of a saved service, or null when its cost isn't set */
const serviceMargin = (s: Service) =>
  hasValue(s.base_price) ? marginOf(num(s.selling_price), num(s.base_price)) : null;

const isUsed = (s: Service) => s.packages_count > 0 || s.events_count > 0 || s.quotations_count > 0;

const usageText = (s: Service) => {
  const parts: string[] = [];
  if (s.packages_count) parts.push(plural(s.packages_count, "package"));
  if (s.quotations_count) parts.push(plural(s.quotations_count, "quotation"));
  if (s.events_count) parts.push(plural(s.events_count, "event"));
  return parts.length ? `in ${parts.join(", ")}` : "not used yet";
};

// ─── Small UI pieces ────────────────────────────────────────────────────────

const PILL_TONES = {
  category: "bg-amber-50 text-amber-800",
  good: "bg-emerald-50 text-emerald-700",
  low: "bg-amber-100 text-amber-800",
  loss: "bg-red-50 text-red-700",
  archived: "bg-slate-100 text-slate-600",
  muted: "bg-stone-100 text-stone-500",
} as const;

function Pill({ tone, children }: { tone: keyof typeof PILL_TONES; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${PILL_TONES[tone]}`}>
      {children}
    </span>
  );
}

function MarginPill({ service }: { service: Service }) {
  const m = serviceMargin(service);
  if (!m) return <Pill tone="muted">No cost set</Pill>;
  if (m.level === "none") return null;
  if (m.level === "loss") return <Pill tone="loss">Below cost</Pill>;
  return <Pill tone={m.level}>{pctText(m.pct)} margin</Pill>;
}

function Switch({ checked, onChange, children, id }: { checked: boolean; onChange: (v: boolean) => void; children: React.ReactNode; id?: string }) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2.5 min-h-10 text-left rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
    >
      <span className={`relative shrink-0 w-10 h-6 rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-stone-300"}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : ""}`} />
      </span>
      <span className="text-sm text-stone-700">{children}</span>
    </button>
  );
}

function Thumb({ image, size = "md" }: { image: string | null; size?: "md" | "lg" }) {
  const box = size === "lg" ? "w-12 h-12" : "w-10 h-10";
  return image ? (
    <img src={getImageUrl(image)} alt="" loading="lazy" className={`${box} shrink-0 rounded-lg object-cover border border-stone-200 bg-stone-50`} />
  ) : (
    <span className={`${box} shrink-0 rounded-lg bg-stone-100 text-stone-300 flex items-center justify-center`}>
      <ImageIcon className="w-4 h-4" />
    </span>
  );
}

const inputCls = (invalid?: boolean) =>
  `w-full h-11 px-3 rounded-xl border text-sm bg-white outline-none focus:ring-2 ${
    invalid ? "border-red-300 focus:border-red-400 focus:ring-red-100" : "border-stone-200 focus:border-amber-400 focus:ring-amber-100"
  }`;
const labelCls = "block text-xs font-semibold text-stone-500 mb-1.5";

function MoneyInput({ id, value, onChange, suffix, invalid }: { id: string; value: string; onChange: (v: string) => void; suffix: string; invalid?: boolean }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-stone-400 pointer-events-none">₹</span>
      <input
        id={id}
        inputMode="decimal"
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.,]/g, ""))}
        placeholder="0"
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? `${id}-error` : undefined}
        className={`${inputCls(invalid)} pl-7 tabular-nums ${suffix ? "pr-20" : ""}`}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-stone-400 pointer-events-none">{suffix}</span>}
    </div>
  );
}

// ─── Create / edit form ─────────────────────────────────────────────────────

/** Server field → input id, so the first error can be focused */
const FIELD_INPUT: Record<string, string> = {
  name: "svc-name",
  category: "svc-category",
  base_price: "svc-cost",
  selling_price: "svc-price",
  description: "svc-description",
};

const MARGIN_BOX: Record<MarginLevel | "empty", string> = {
  good: "bg-emerald-50 text-emerald-800",
  low: "bg-amber-50 text-amber-800",
  loss: "bg-red-50 text-red-700",
  none: "bg-stone-50 text-stone-600",
  empty: "bg-stone-50 text-stone-500",
};

function ServiceFormModal({
  service,
  services,
  vendors,
  categories,
  onClose,
  onSaved,
}: {
  service: Service | null;
  services: Service[];
  vendors: VendorOption[];
  categories: string[];
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSaved: (raw: any) => void;
}) {
  const { showToast } = useToast();
  const isEdit = !!service;
  const nameRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(service?.name ?? "");
  const [category, setCategory] = useState(service?.category ?? "");
  const [unit, setUnit] = useState<ChargeUnit>(toUnit(service?.unit));
  const [cost, setCost] = useState(service && hasValue(service.base_price) ? String(num(service.base_price)) : "");
  const [price, setPrice] = useState(service ? String(num(service.selling_price)) : "");
  const [vendorId, setVendorId] = useState(service?.vendor_id ? String(service.vendor_id) : "");
  const [description, setDescription] = useState(service?.description ?? "");
  const [isActive, setIsActive] = useState(service?.is_active ?? true);
  const [photo, setPhoto] = useState<File | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [replacingPhoto, setReplacingPhoto] = useState(false);
  // null = follow whether the price changed; true/false once the user ticks the box
  const [updatePackages, setUpdatePackages] = useState<boolean | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) setTimeout(() => nameRef.current?.focus(), 50);
  }, [isEdit]);

  const photoPreview = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);
  useEffect(() => () => { if (photoPreview) URL.revokeObjectURL(photoPreview); }, [photoPreview]);

  const suffix = unitSuffix(unit);
  const priceNum = parseMoney(price);
  const costNum = parseMoney(cost);
  const priceOk = priceNum !== null && !Number.isNaN(priceNum);
  const costOk = costNum !== null && !Number.isNaN(costNum);
  const margin = priceOk && costOk ? marginOf(priceNum, costNum) : null;

  const priceChanged =
    !!service &&
    (num(service.selling_price) !== priceNum ||
      (hasValue(service.base_price) ? num(service.base_price) : null) !== costNum ||
      toUnit(service.unit) !== unit);
  const updatePackagesChecked = updatePackages ?? priceChanged;

  const typedCategory = category.trim();
  const matchedCategory = typedCategory ? categories.find((c) => normalizeName(c) === normalizeName(typedCategory)) : undefined;

  const vendorOptions = useMemo(() => {
    const list = [...vendors];
    if (service?.vendor && !list.some((v) => v.id === service.vendor!.id)) list.push(service.vendor);
    return [
      { value: "", label: "No vendor (in-house)" },
      ...list.sort((a, b) => a.name.localeCompare(b.name)).map((v) => ({ value: String(v.id), label: v.name })),
    ];
  }, [vendors, service]);

  const clearError = (key: string) => setErrors((e) => (e[key] ? { ...e, [key]: "" } : e));
  const focusFirst = (errs: Record<string, string>) => {
    const first = Object.keys(FIELD_INPUT).find((k) => errs[k]);
    if (first) setTimeout(() => document.getElementById(FIELD_INPUT[first])?.focus(), 0);
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    const trimmed = name.trim();
    if (!trimmed) {
      next.name = "Enter the service name";
    } else {
      const key = normalizeName(trimmed);
      const dup = key ? services.find((s) => s.id !== service?.id && normalizeName(s.name) === key) : undefined;
      if (dup) next.name = `A service called “${dup.name}” already exists.`;
    }
    if (priceNum === null) next.selling_price = "Enter the selling price";
    else if (Number.isNaN(priceNum) || priceNum < 0) next.selling_price = "Enter a valid amount";
    if (costNum !== null && (Number.isNaN(costNum) || costNum < 0)) next.base_price = "Enter a valid amount";
    setErrors(next);
    focusFirst(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (saving || !validate()) return;
    setSaving(true);
    try {
      const form = new FormData();
      form.append("name", name.trim());
      form.append("category", typedCategory);
      form.append("description", description.trim());
      form.append("selling_price", String(priceNum));
      form.append("base_price", costNum === null ? "" : String(costNum));
      form.append("unit", unit);
      form.append("vendor_id", vendorId);
      form.append("is_active", isActive ? "1" : "0");
      if (photo) form.append("image_file", photo);
      else if (removeImage) form.append("remove_image", "1");
      if (service && service.packages_count > 0) form.append("update_packages", updatePackagesChecked ? "1" : "0");

      const res = await mastersApi.services.save(service?.id, form);
      showToast(res?.message || (isEdit ? "Service updated" : "Service created"), "success");
      onSaved(res?.data ?? null);
      onClose();
    } catch (err) {
      // Keep the form open with what was typed, and show the server's reasons next to the fields
      const fieldErrors: Record<string, string> = {};
      Object.entries((err as ApiError).errors || {}).forEach(([field, msgs]) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs[0] : String(msgs);
      });
      setErrors(fieldErrors);
      focusFirst(fieldErrors);
      showToast(apiErrorMessage(err, "Couldn't save the service"), "error");
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (key: string, id?: string) =>
    errors[key] ? <p id={id ? `${id}-error` : undefined} className="text-xs text-red-600 mt-1">{errors[key]}</p> : null;

  const footer = (
    <div className="flex gap-2 sm:justify-end">
      <button
        type="button"
        onClick={onClose}
        disabled={saving}
        className="flex-1 sm:flex-none sm:min-w-28 h-11 px-4 rounded-xl border border-stone-300 text-sm font-semibold text-stone-600 hover:bg-stone-50 disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={submit}
        disabled={saving}
        className="flex-1 sm:flex-none sm:min-w-36 h-11 px-5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {saving && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />}
        {saving ? "Saving…" : isEdit ? "Save changes" : "Save service"}
      </button>
    </div>
  );

  const marginTone: MarginLevel | "empty" = margin ? margin.level : "empty";

  return (
    <Modal isOpen onClose={() => !saving && onClose()} title={isEdit ? "Edit service" : "Add service"} size="lg" footer={footer}>
      <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-5" noValidate>
        {/* Name + category */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="svc-name" className={labelCls}>Service name *</label>
            <input
              id="svc-name"
              ref={nameRef}
              value={name}
              onChange={(e) => { setName(e.target.value); clearError("name"); }}
              placeholder="e.g. Veg buffet (Gold menu)"
              autoComplete="off"
              aria-invalid={!!errors.name || undefined}
              aria-describedby={errors.name ? "svc-name-error" : undefined}
              className={inputCls(!!errors.name)}
            />
            {fieldError("name", "svc-name")}
          </div>
          <div>
            <label htmlFor="svc-category" className={labelCls}>
              Category <span className="font-normal">(pick or type a new one)</span>
            </label>
            <input
              id="svc-category"
              list="svc-category-options"
              value={category}
              onChange={(e) => { setCategory(e.target.value); clearError("category"); }}
              placeholder="e.g. Catering"
              autoComplete="off"
              className={inputCls(!!errors.category)}
            />
            <datalist id="svc-category-options">
              {categories.map((c) => <option key={c} value={c} />)}
            </datalist>
            {errors.category ? (
              fieldError("category")
            ) : matchedCategory && matchedCategory !== typedCategory ? (
              <p className="text-[11px] text-stone-500 mt-1">Will be saved as the existing “{matchedCategory}”.</p>
            ) : typedCategory && !matchedCategory && categories.length > 0 ? (
              <p className="text-[11px] text-stone-400 mt-1">New category</p>
            ) : null}
          </div>
        </div>

        {/* How it's charged */}
        <div>
          <span className={labelCls} id="svc-unit-label">Charged</span>
          <div className="flex flex-wrap gap-2" role="group" aria-labelledby="svc-unit-label">
            {CHARGE_UNITS.map((u) => (
              <button
                key={u.value}
                type="button"
                aria-pressed={unit === u.value}
                onClick={() => setUnit(u.value)}
                className={`min-h-10 px-3.5 rounded-full border text-sm transition-colors ${
                  unit === u.value
                    ? "border-amber-500 bg-amber-50 text-amber-800 font-semibold"
                    : "border-stone-200 bg-white text-stone-600 hover:border-amber-300 hover:bg-amber-50/50"
                }`}
              >
                {u.label}
              </button>
            ))}
          </div>
          {fieldError("unit")}
        </div>

        {/* Prices */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="svc-cost" className={labelCls}>Cost to us</label>
            <MoneyInput id="svc-cost" value={cost} onChange={(v) => { setCost(v); clearError("base_price"); }} suffix={suffix} invalid={!!errors.base_price} />
            {fieldError("base_price", "svc-cost")}
          </div>
          <div>
            <label htmlFor="svc-price" className={labelCls}>Selling price *</label>
            <MoneyInput id="svc-price" value={price} onChange={(v) => { setPrice(v); clearError("selling_price"); }} suffix={suffix} invalid={!!errors.selling_price} />
            {fieldError("selling_price", "svc-price")}
          </div>
        </div>

        {/* Live margin */}
        <div role="status" aria-live="polite" className={`rounded-xl px-3.5 py-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm ${MARGIN_BOX[marginTone]}`}>
          {!margin ? (
            <span>{priceOk ? "Add what it costs us to see the margin" : "Enter the prices to see the margin"}</span>
          ) : (
            <>
              <span className={margin.level === "loss" ? "font-semibold" : ""}>
                {margin.level === "loss" ? "Selling below cost: you lose money on every sale" : margin.level === "low" ? "Low margin" : "Margin"}
              </span>
              <strong className="text-base tabular-nums">
                {money(margin.amount)}{suffix ? ` ${suffix}` : ""}
                {priceNum! > 0 && ` · ${pctText(margin.pct)}`}
              </strong>
            </>
          )}
        </div>

        {/* Vendor + photo */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <span className={labelCls}>Default vendor</span>
            <SearchableDropdown options={vendorOptions} value={vendorId} onChange={(v) => { setVendorId(v); clearError("vendor_id"); }} placeholder="No vendor (in-house)" error={errors.vendor_id || undefined} />
            <p className="text-[11px] text-stone-400 mt-1">Filled in when this service is added to a package or event.</p>
          </div>
          <div>
            <span className={labelCls}>Photo</span>
            {photo && photoPreview ? (
              <div className="flex items-center gap-3 p-2 rounded-xl border border-stone-200 bg-stone-50">
                <img src={photoPreview} alt="New photo" className="w-14 h-14 rounded-lg object-cover border border-stone-200" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-stone-700">New photo</p>
                  <p className="text-[11px] text-stone-500 truncate">{photo.name}</p>
                </div>
                <button type="button" onClick={() => setPhoto(null)} className="h-10 px-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50">
                  Remove
                </button>
              </div>
            ) : service?.image && !removeImage && !replacingPhoto ? (
              <div className="flex items-center gap-3 p-2 rounded-xl border border-stone-200 bg-stone-50">
                <img src={getImageUrl(service.image)} alt={`Current photo of ${service.name}`} className="w-14 h-14 rounded-lg object-cover border border-stone-200" />
                <p className="min-w-0 flex-1 text-sm font-medium text-stone-700">Current photo</p>
                <button type="button" onClick={() => setReplacingPhoto(true)} className="h-10 px-3 rounded-lg text-sm font-medium text-stone-600 hover:bg-stone-100">
                  Change
                </button>
                <button type="button" onClick={() => setRemoveImage(true)} className="h-10 px-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50">
                  Remove
                </button>
              </div>
            ) : (
              <>
                <ImageUpload
                  selectedFile={null}
                  onFileSelect={(f) => {
                    if (!f) return;
                    setPhoto(f);
                    setReplacingPhoto(false);
                    clearError("image_file");
                  }}
                  label="Tap to add a photo"
                  description="Shown in packages and quotations"
                  acceptedFormats={["image/jpeg", "image/png", "image/webp"]}
                  maxSize={5 * 1024 * 1024}
                  compressToWebP
                />
                {service?.image && (removeImage || replacingPhoto) && (
                  <p className="text-[11px] text-stone-500 mt-1">
                    {removeImage ? "The current photo will be removed when you save. " : "Pick a new photo to replace the current one. "}
                    <button type="button" onClick={() => { setRemoveImage(false); setReplacingPhoto(false); }} className="font-semibold text-amber-700 hover:underline">
                      {removeImage ? "Undo" : "Keep current"}
                    </button>
                  </p>
                )}
              </>
            )}
            {fieldError("image_file")}
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="svc-description" className={labelCls}>
            Description <span className="font-normal">(optional)</span>
          </label>
          <textarea
            id="svc-description"
            value={description}
            onChange={(e) => { setDescription(e.target.value); clearError("description"); }}
            rows={2}
            placeholder="e.g. 3 starters, 2 mains, dal, rice, 2 desserts"
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
          />
          {fieldError("description")}
        </div>

        {/* Packages that use this service */}
        {service && service.packages_count > 0 && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-sm text-slate-700 space-y-1.5">
            <p className="flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
              <span>
                Used in <b>{plural(service.packages_count, "package")}</b>
                {service.events_count > 0 && <> and {plural(service.events_count, "event")}</>}.
              </span>
            </p>
            <label className="flex items-center gap-2.5 min-h-10 cursor-pointer text-stone-700">
              <input
                type="checkbox"
                checked={updatePackagesChecked}
                onChange={(e) => setUpdatePackages(e.target.checked)}
                className="w-[18px] h-[18px] accent-amber-600"
              />
              Also update the price in those packages
            </label>
            <p className="text-xs text-slate-500">Quotations and events already made keep their price.</p>
          </div>
        )}

        {/* Active */}
        <div>
          <Switch checked={isActive} onChange={setIsActive}>
            {isActive ? (
              <><b className="font-semibold">Active</b>: shown when building packages and quotations</>
            ) : (
              <><b className="font-semibold">Archived</b>: hidden from new packages and quotations, kept on old ones</>
            )}
          </Switch>
        </div>
      </form>
    </Modal>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function ServicesPage() {
  const { showToast } = useToast();

  const [services, setServices] = useState<Service[]>([]);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [sort, setSort] = useState<SortKey>("newest");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Service | null>(null);
  const [deleting, setDeleting] = useState<Service | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const loadServices = async () => {
    try {
      const res = await mastersApi.services.list();
      setServices(listOf(res).map(toService));
      setLoadFailed(false);
    } catch (error) {
      setLoadFailed(true);
      showToast(apiErrorMessage(error, "Couldn't load services"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServices();
    vendorsApi
      .list("for_select=1")
      .then((res) => setVendors(listOf(res).map((v) => ({ id: Number(v.id), name: String(v.name ?? "") }))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every category in use (archived services included), most used first, for the form's suggestions
  const allCategories = useMemo(() => {
    const counts = new Map<string, number>();
    services.forEach((s) => s.category && counts.set(s.category, (counts.get(s.category) || 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([c]) => c);
  }, [services]);

  const stats = useMemo(
    () => ({
      total: services.length,
      active: services.filter((s) => s.is_active).length,
      withVendor: services.filter((s) => s.vendor_id).length,
    }),
    [services]
  );
  const archivedCount = stats.total - stats.active;

  const visible = useMemo(() => (showArchived ? services : services.filter((s) => s.is_active)), [services, showArchived]);

  const categoryChips = useMemo(() => {
    const counts = new Map<string, number>();
    let none = 0;
    visible.forEach((s) => (s.category ? counts.set(s.category, (counts.get(s.category) || 0) + 1) : none++));
    // Keep the selected chip visible (with 0) so it can be switched off
    if (category !== "all" && category !== UNCATEGORISED && !counts.has(category)) counts.set(category, 0);
    return { cats: [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0])), none };
  }, [visible, category]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = visible.filter((s) => {
      if (category === UNCATEGORISED) {
        if (s.category) return false;
      } else if (category !== "all" && s.category !== category) {
        return false;
      }
      if (!q) return true;
      return [s.name, s.description, s.category, s.vendor?.name].some((v) => (v || "").toLowerCase().includes(q));
    });

    // Services without a cost go last when sorting by margin
    const byMargin = (a: Service, b: Service, dir: 1 | -1) => {
      const ma = serviceMargin(a)?.pct ?? null;
      const mb = serviceMargin(b)?.pct ?? null;
      if (ma === null || mb === null) return ma === mb ? 0 : ma === null ? 1 : -1;
      return (ma - mb) * dir;
    };

    switch (sort) {
      case "name":
        return [...list].sort((a, b) => a.name.localeCompare(b.name));
      case "price_desc":
        return [...list].sort((a, b) => num(b.selling_price) - num(a.selling_price));
      case "price_asc":
        return [...list].sort((a, b) => num(a.selling_price) - num(b.selling_price));
      case "margin_asc":
        return [...list].sort((a, b) => byMargin(a, b, 1));
      case "margin_desc":
        return [...list].sort((a, b) => byMargin(a, b, -1));
      default:
        return [...list].sort(byNewest);
    }
  }, [visible, category, search, sort]);

  const hasFilters = !!search.trim() || category !== "all";
  const clearFilters = () => { setSearch(""); setCategory("all"); };

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (s: Service) => { setEditing(s); setFormOpen(true); };
  const closeForm = () => { setFormOpen(false); setEditing(null); };

  /** Merge a saved service from the server into the list (the save response has no usage counts) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSaved = (raw: any) => {
    if (!raw?.id) {
      loadServices();
      return;
    }
    setServices((prev) => {
      const old = prev.find((s) => s.id === Number(raw.id));
      // The save response carries the usage counts; fall back to the old row's if it ever doesn't
      const next = toService({
        ...old,
        ...raw,
        packages_count: raw.packages_count ?? old?.packages_count ?? 0,
        events_count: raw.events_count ?? old?.events_count ?? 0,
        quotations_count: raw.quotations_count ?? old?.quotations_count ?? 0,
      });
      return old ? prev.map((s) => (s.id === next.id ? next : s)) : [next, ...prev];
    });
  };

  const setActive = async (s: Service, active: boolean) => {
    if (busyId) return;
    setBusyId(s.id);
    try {
      const form = new FormData();
      form.append("is_active", active ? "1" : "0");
      const res = await mastersApi.services.save(s.id, form);
      handleSaved(res?.data ? { ...res.data, is_active: res.data.is_active ?? active } : { id: s.id, is_active: active });
      showToast(
        active ? `“${s.name}” restored` : `“${s.name}” archived. It stays on quotations and events already made.`,
        "success"
      );
    } catch (error) {
      showToast(apiErrorMessage(error, active ? "Couldn't restore the service" : "Couldn't archive the service"), "error");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const target = deleting;
    setDeleteLoading(true);
    try {
      await mastersApi.services.delete(target.id);
      setServices((prev) => prev.filter((s) => s.id !== target.id));
      showToast(`“${target.name}” deleted`, "success");
    } catch (error) {
      // e.g. used on a quotation: the server says to archive it instead
      showToast(apiErrorMessage(error, "Couldn't delete the service"), "error");
    } finally {
      setDeleteLoading(false);
      setDeleting(null);
    }
  };

  const chipCls = (active: boolean) =>
    `shrink-0 min-h-10 px-3.5 rounded-full border text-sm whitespace-nowrap transition-colors ${
      active ? "border-amber-500 bg-amber-50 text-amber-800 font-semibold" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"
    }`;

  const categoryPill = (s: Service) =>
    s.category ? <Pill tone="category">{s.category}</Pill> : null;

  /** Edit always; Delete only for never-used services, otherwise Archive / Restore */
  const actions = (s: Service, mobile = false) => {
    const busy = busyId === s.id;
    const btn = mobile
      ? "h-10 min-w-10 px-3 inline-flex items-center justify-center gap-1.5 rounded-lg border border-stone-200 text-sm font-medium disabled:opacity-50"
      : "w-9 h-9 inline-flex items-center justify-center rounded-lg disabled:opacity-50";
    const toggleArchive = !s.is_active || isUsed(s);
    return (
      <div className={`flex items-center ${mobile ? "gap-2 justify-end flex-wrap" : "gap-0.5 justify-end"}`}>
        <button type="button" onClick={() => openEdit(s)} aria-label={`Edit ${s.name}`} title="Edit" className={`${btn} text-stone-600 hover:text-amber-700 hover:bg-amber-50`}>
          <Pencil className="w-4 h-4" />
          {mobile && "Edit"}
        </button>
        {toggleArchive && (
          <button
            type="button"
            onClick={() => setActive(s, !s.is_active)}
            disabled={busy}
            aria-label={`${s.is_active ? "Archive" : "Restore"} ${s.name}`}
            title={s.is_active ? "Archive: hide from new packages and quotations" : "Restore"}
            className={`${btn} ${s.is_active ? "text-stone-600 hover:text-slate-800 hover:bg-slate-100" : "text-emerald-700 hover:bg-emerald-50"}`}
          >
            {busy ? (
              <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" aria-hidden="true" />
            ) : s.is_active ? (
              <Archive className="w-4 h-4" />
            ) : (
              <ArchiveRestore className="w-4 h-4" />
            )}
            {mobile && (s.is_active ? "Archive" : "Restore")}
          </button>
        )}
        {!isUsed(s) && (
          <button type="button" onClick={() => setDeleting(s)} aria-label={`Delete ${s.name}`} title="Delete" className={`${btn} text-stone-500 hover:text-red-600 hover:bg-red-50`}>
            <Trash2 className="w-4 h-4" />
            {mobile && "Delete"}
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64" role="status" aria-label="Loading services">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent" />
        </div>
      </DashboardLayout>
    );
  }

  const emptyState = (() => {
    const wrap = (title: string, body: React.ReactNode, action: React.ReactNode) => (
      <div className="text-center py-12 px-4 bg-white rounded-xl border border-stone-200">
        <Package className="w-12 h-12 text-stone-300 mx-auto mb-3" />
        <p className="text-stone-700 font-semibold">{title}</p>
        {body && <p className="text-sm text-stone-500 mt-1 max-w-md mx-auto">{body}</p>}
        <div className="mt-4">{action}</div>
      </div>
    );
    if (loadFailed && services.length === 0) {
      return wrap(
        "Couldn't load services",
        "Check your connection and try again.",
        <button onClick={() => { setLoading(true); loadServices(); }} className="h-10 inline-flex items-center gap-2 px-4 rounded-xl border border-stone-300 text-sm font-semibold text-stone-700 hover:bg-stone-50">
          <RefreshCw className="w-4 h-4" /> Try again
        </button>
      );
    }
    if (services.length === 0) {
      return wrap(
        "No services yet",
        "Add what you sell once (catering per guest, décor, photography…) and reuse it in packages and quotations.",
        <button onClick={openCreate} className="h-10 inline-flex items-center gap-2 px-4 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold text-sm">
          <Plus className="w-4 h-4" /> Add the first service
        </button>
      );
    }
    if (visible.length === 0) {
      return wrap(
        `All ${plural(archivedCount, "service")} ${archivedCount === 1 ? "is" : "are"} archived`,
        null,
        <button onClick={() => setShowArchived(true)} className="text-sm font-semibold text-amber-700 hover:underline">Show archived</button>
      );
    }
    return wrap(
      "No services match",
      null,
      <button onClick={clearFilters} className="text-sm font-semibold text-amber-700 hover:underline">Clear filters</button>
    );
  })();

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* The Topbar shows this subtitle from lg up */}
        <p className="text-sm text-stone-500 lg:hidden">What you sell, how it's charged, and the margin on each.</p>
        <button onClick={openCreate} className="h-10 w-full sm:w-auto sm:ml-auto flex items-center justify-center gap-2 px-4 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold text-sm">
          <Plus className="w-4 h-4" /> Add service
        </button>
      </div>

      {/* Summary */}
      {services.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
          {[
            { label: "Services", value: stats.total },
            { label: "Active", value: stats.active },
            { label: "With a vendor", value: stats.withVendor },
          ].map((c) => (
            <div key={c.label} className="bg-white rounded-xl border border-stone-200 px-3 sm:px-4 py-2.5">
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wide text-stone-400 truncate">{c.label}</p>
              <p className="text-xl sm:text-2xl font-bold text-stone-800 tabular-nums">{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {services.length > 0 && (
        <>
          {/* Search, sort, archived */}
          <div className="bg-white rounded-xl border border-stone-200 p-3 mb-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, description, category, vendor…"
                aria-label="Search services"
                className="w-full h-10 pl-9 pr-3 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              />
            </div>
            <div className="flex flex-wrap items-center justify-between sm:justify-end gap-x-3 gap-y-1">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                aria-label="Sort services"
                className="h-10 px-3 rounded-xl border border-stone-200 text-sm bg-white text-stone-700 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
              >
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {archivedCount > 0 && (
                <Switch checked={showArchived} onChange={setShowArchived}>
                  <span className="whitespace-nowrap">Show archived ({archivedCount})</span>
                </Switch>
              )}
            </div>
          </div>

          {/* Category chips */}
          {(categoryChips.cats.length > 0) && (
            <div className="flex gap-2 overflow-x-auto pb-2 mb-2 -mx-1 px-1" role="group" aria-label="Filter by category">
              <button type="button" aria-pressed={category === "all"} onClick={() => setCategory("all")} className={chipCls(category === "all")}>
                All <span className="text-stone-400 tabular-nums">{visible.length}</span>
              </button>
              {categoryChips.cats.map(([name, count]) => (
                <button key={name} type="button" aria-pressed={category === name} onClick={() => setCategory(category === name ? "all" : name)} className={chipCls(category === name)}>
                  {name} <span className="text-stone-400 tabular-nums">{count}</span>
                </button>
              ))}
              {(categoryChips.none > 0 || category === UNCATEGORISED) && (
                <button type="button" aria-pressed={category === UNCATEGORISED} onClick={() => setCategory(category === UNCATEGORISED ? "all" : UNCATEGORISED)} className={chipCls(category === UNCATEGORISED)}>
                  Uncategorised <span className="text-stone-400 tabular-nums">{categoryChips.none}</span>
                </button>
              )}
            </div>
          )}

          {hasFilters && filtered.length > 0 && (
            <div className="flex items-center justify-between gap-3 mb-3 text-sm">
              <span className="text-stone-500">{filtered.length} of {visible.length} shown</span>
              <button type="button" onClick={clearFilters} className="h-9 inline-flex items-center gap-1 px-3 rounded-lg text-stone-600 hover:bg-stone-100">
                <X className="w-4 h-4" /> Clear filters
              </button>
            </div>
          )}
        </>
      )}

      {filtered.length === 0 ? (
        emptyState
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          {/* Mobile cards */}
          <ul className="md:hidden divide-y divide-stone-100">
            {filtered.map((s) => (
              <li key={s.id} className={`p-4 ${s.is_active ? "" : "bg-stone-50/80"}`}>
                <div className="flex items-start gap-3">
                  {s.image && <Thumb image={s.image} size="lg" />}
                  <div className="min-w-0 flex-1">
                    <p className={`font-semibold break-words ${s.is_active ? "text-stone-800" : "text-stone-500"}`}>{s.name}</p>
                    {s.description && <p className="text-xs text-stone-500 mt-0.5 line-clamp-2 break-words">{s.description}</p>}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-bold text-stone-800 tabular-nums">{formatCurrencyFull(s.selling_price)}</p>
                    <p className="text-[11px] text-stone-500">{unitWords(s.unit)}</p>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-stone-500">
                  {!s.is_active && <Pill tone="archived">Archived</Pill>}
                  {categoryPill(s)}
                  {s.vendor && (
                    <span className="inline-flex items-center gap-1 min-w-0">
                      <Store className="w-3 h-3 shrink-0" /> <span className="truncate">{s.vendor.name}</span>
                    </span>
                  )}
                  <MarginPill service={s} />
                  <span>· {usageText(s)}</span>
                </div>
                <div className="mt-3">{actions(s, true)}</div>
              </li>
            ))}
          </ul>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-amber-50 border-b-2 border-amber-200">
                <tr>
                  {[
                    ["Service", "text-left"],
                    ["Category", "text-left"],
                    ["Price", "text-right"],
                    ["Cost", "text-right"],
                    ["Margin", "text-right"],
                    ["Vendor", "text-left hidden lg:table-cell"],
                    ["Used in", "text-left hidden lg:table-cell"],
                  ].map(([label, cls]) => (
                    <th key={label} className={`${cls} px-4 py-3 text-[10px] font-bold text-stone-600 uppercase tracking-wide`}>{label}</th>
                  ))}
                  <th className="px-4 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((s) => {
                  const m = serviceMargin(s);
                  const per = unitSuffix(s.unit);
                  return (
                    <tr key={s.id} className={s.is_active ? "bg-white hover:bg-stone-50/60" : "bg-stone-50/70"}>
                      <td className="px-4 py-3 max-w-[320px]">
                        <div className="flex items-center gap-3">
                          <Thumb image={s.image} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className={`font-semibold text-sm truncate ${s.is_active ? "text-stone-800" : "text-stone-500"}`}>{s.name}</span>
                              {!s.is_active && <Pill tone="archived">Archived</Pill>}
                            </div>
                            {s.description && <div className="text-xs text-stone-500 truncate">{s.description}</div>}
                            <div className="lg:hidden text-[11px] text-stone-400 truncate">
                              {[s.vendor?.name, usageText(s)].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">{categoryPill(s) ?? <span className="text-stone-300">-</span>}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="font-bold text-sm text-stone-800 tabular-nums">{formatCurrencyFull(s.selling_price)}</div>
                        <div className="text-[11px] text-stone-500">{unitWords(s.unit)}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-stone-600 tabular-nums whitespace-nowrap">
                        {hasValue(s.base_price) ? formatCurrencyFull(s.base_price) : <span className="text-stone-300">-</span>}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <MarginPill service={s} />
                        {m && m.level !== "none" && (
                          <div className={`text-[11px] tabular-nums mt-0.5 ${m.level === "loss" ? "text-red-600" : "text-stone-500"}`}>
                            {money(m.amount)}{per ? ` ${per}` : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-stone-600 hidden lg:table-cell">
                        {s.vendor?.name || <span className="text-stone-300">-</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-stone-600 hidden lg:table-cell whitespace-nowrap">
                        {isUsed(s) ? (
                          <>
                            {s.packages_count > 0 && <div>{plural(s.packages_count, "package")}</div>}
                            {s.quotations_count > 0 && <div>{plural(s.quotations_count, "quotation")}</div>}
                            {s.events_count > 0 && <div>{plural(s.events_count, "event")}</div>}
                          </>
                        ) : (
                          <span className="text-stone-400">Not used yet</span>
                        )}
                      </td>
                      <td className="px-3 py-3">{actions(s)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {formOpen && (
        <ServiceFormModal
          key={editing?.id ?? "new"}
          service={editing}
          services={services}
          vendors={vendors}
          categories={allCategories}
          onClose={closeForm}
          onSaved={handleSaved}
        />
      )}

      <DeleteConfirmationModal
        isOpen={!!deleting}
        onClose={() => !deleteLoading && setDeleting(null)}
        onConfirm={confirmDelete}
        title="Delete service"
        message="This service has never been used in a package or event. Delete it? This can't be undone."
        itemName={deleting?.name}
        loading={deleteLoading}
      />
    </DashboardLayout>
  );
}
